import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";
import { api, yen } from "../lib/api";

const KUMAMOTO = [32.8032, 130.7079];

const radiusFor = (count, max) => 8 + Math.round((count / Math.max(1, max)) * 22);

/** Sesuaikan tampilan peta agar semua gelembung terlihat. */
const FitBounds = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (positions.length === 0) return;
    if (positions.length === 1) map.setView(positions[0], 10);
    else map.fitBounds(positions, { padding: [40, 40] });
  }, [map, positions]);
  return null;
};

/** Peta sebaran kota pemesan online (OpenStreetMap). */
export const BuyerMapCard = ({ start, end }) => {
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    api
      .get("/reports/buyer-map", { params: { start, end } })
      .then(({ data }) => setD(data))
      .catch(() => {
        setD(null);
        setError(true);
      });
  }, [start, end]);

  const mapped = useMemo(
    () => (d?.cities || []).filter((c) => c.lat != null && c.lng != null),
    [d]
  );
  const points = useMemo(
    () => (d?.points || []).filter((p) => p.lat != null && p.lng != null),
    [d]
  );
  const positions = useMemo(
    () => [...mapped.map((c) => [c.lat, c.lng]), ...points.map((p) => [p.lat, p.lng])],
    [mapped, points]
  );
  const max = Math.max(1, ...mapped.map((c) => c.count));

  if (error)
    return (
      <div
        className="bg-white border border-gray-200 rounded-2xl p-5"
        data-testid="buyer-map-card"
      >
        <p className="font-display text-lg font-semibold flex items-center gap-2">
          <MapPin size={18} className="text-primary" /> Sebaran Kota Pemesan
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          Gagal memuat data sebaran pembeli. Coba muat ulang halaman.
        </p>
      </div>
    );

  if (!d)
    return (
      <div
        className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4"
        data-testid="buyer-map-card"
      >
        <p className="font-display text-lg font-semibold flex items-center gap-2">
          <MapPin size={18} className="text-primary" /> Sebaran Kota Pemesan
        </p>
        <div className="h-72 rounded-2xl bg-gray-100 animate-pulse" />
      </div>
    );

  return (
    <div
      className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4"
      data-testid="buyer-map-card"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-display text-lg font-semibold flex items-center gap-2">
          <MapPin size={18} className="text-primary" /> Sebaran Kota Pemesan
        </p>
        <span className="text-xs text-muted-foreground num">
          {d.total_orders} pesanan · {start} — {end}
        </span>
      </div>

      <div className="h-72 lg:h-96 rounded-2xl overflow-hidden border border-gray-200">
        <MapContainer
          center={positions[0] || KUMAMOTO}
          zoom={positions.length > 1 ? 7 : 10}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          <FitBounds positions={positions} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {mapped.map((c) => (
            <CircleMarker
              key={c.name}
              center={[c.lat, c.lng]}
              radius={radiusFor(c.count, max)}
              pathOptions={{ color: "#107C56", fillColor: "#107C56", fillOpacity: 0.45 }}
            >
              <Tooltip permanent={mapped.length <= 4} direction="top">
                {c.name}: {c.count} pesanan · {yen(c.revenue)}
              </Tooltip>
            </CircleMarker>
          ))}
          {points.map((p, i) => (
            <CircleMarker
              key={`pt-${i}`}
              center={[p.lat, p.lng]}
              radius={4}
              pathOptions={{ color: "#C2410C", fillColor: "#C2410C", fillOpacity: 0.9 }}
            >
              <Tooltip direction="top">
                {p.order_no} · {p.label}
              </Tooltip>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>

      <div className="space-y-2" data-testid="buyer-city-rank">
        {(d.cities || []).length === 0 && (          <p className="text-sm text-muted-foreground">Belum ada pesanan online pada rentang ini</p>
        )}
        {(d.cities || []).map((c) => (
          <div key={`rank-${c.name}`} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="truncate">
                {c.name}
                {!c.lat && <span className="text-muted-foreground"> (belum ada titik peta)</span>}
              </span>
              <span className="num text-muted-foreground">
                {c.count} pesanan · {yen(c.revenue)}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-500"
                style={{ width: `${(c.count / Math.max(1, d.total_orders)) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Titik jingga adalah lokasi tepat pembeli yang menekan &quot;Bagikan lokasi&quot; saat
        memesan. Kota yang belum dikenal koordinatnya tetap muncul di daftar peringkat.
      </p>
    </div>
  );
};
